import React, { useRef, useCallback, useState } from 'react';

const GlowCard = ({ 
  children, 
  className = '', 
  glowColor = 'rgba(138, 100, 255, 0.8)',
  glowSize = 200,
  borderRadius = '0px'
}) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [glowPosition, setGlowPosition] = useState({ x: 50, y: 50, angle: 0, distance: 0 });

  const clamp = (value, min = 0, max = 100) => Math.min(Math.max(value, min), max);

  const centerOfElement = useCallback((rect) => {
    return [rect.width / 2, rect.height / 2];
  }, []);

  const distanceFromCenter = useCallback((rect, x, y) => {
    const [cx, cy] = centerOfElement(rect);
    return [x - cx, y - cy];
  }, [centerOfElement]);

  const closenessToEdge = useCallback((rect, x, y) => {
    const [cx, cy] = centerOfElement(rect);
    const [dx, dy] = distanceFromCenter(rect, x, y);
    let k_x = Infinity;
    let k_y = Infinity;
    if (dx !== 0) {
      k_x = cx / Math.abs(dx);
    }
    if (dy !== 0) {
      k_y = cy / Math.abs(dy);
    }
    return clamp(1 / Math.min(k_x, k_y), 0, 1);
  }, [centerOfElement, distanceFromCenter]);

  const angleFromPointer = useCallback((dx, dy) => {
    let angleRadians = 0;
    let angleDegrees = 0;
    if (dx !== 0 || dy !== 0) {
      angleRadians = Math.atan2(dy, dx);
      angleDegrees = angleRadians * (180 / Math.PI) + 90;
      if (angleDegrees < 0) {
        angleDegrees += 360;
      }
    }
    return angleDegrees;
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const px = clamp((100 / rect.width) * x);
    const py = clamp((100 / rect.height) * y);
    
    const [dx, dy] = distanceFromCenter(rect, x, y);
    const edge = closenessToEdge(rect, x, y);
    const angle = angleFromPointer(dx, dy);

    setGlowPosition({
      x: px,
      y: py,
      angle: angle,
      distance: edge * 100
    });
  }, [distanceFromCenter, closenessToEdge, angleFromPointer]);

  const handlePointerEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handlePointerLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <div
      ref={cardRef}
      className={`glow-card ${className}`}
      onPointerMove={handlePointerMove}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      style={{
        '--pointer-x': `${glowPosition.x}%`,
        '--pointer-y': `${glowPosition.y}%`,
        '--pointer-angle': `${glowPosition.angle}deg`,
        '--pointer-distance': glowPosition.distance,
        '--glow-color': glowColor,
        '--glow-size': `${glowSize}px`,
        '--card-radius': borderRadius,
        position: 'relative',
        borderRadius: borderRadius,
        isolation: 'isolate'
      }}
    >
      {/* Main Glow Effect - follows cursor */}
      <span 
        className="glow-effect"
        style={{
          position: 'absolute',
          inset: '-2px',
          borderRadius: 'inherit',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
          zIndex: 1,
          background: `
            radial-gradient(
              ${glowSize}px circle at var(--pointer-x) var(--pointer-y),
              ${glowColor},
              transparent 60%
            )
          `,
          filter: 'blur(2px)'
        }}
      />
      
      {/* Conic gradient border glow */}
      <span 
        className="border-glow"
        style={{
          position: 'absolute',
          inset: '-1px',
          borderRadius: 'inherit',
          opacity: isHovered ? 0.8 : 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
          zIndex: 0,
          background: `
            conic-gradient(
              from calc(var(--pointer-angle) - 45deg) at var(--pointer-x) var(--pointer-y),
              transparent 0%,
              ${glowColor} 10%,
              transparent 20%
            )
          `,
          filter: 'blur(8px)'
        }}
      />

      {/* Inner highlight effect */}
      <span 
        className="inner-glow"
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          opacity: isHovered ? 0.15 : 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
          zIndex: 1,
          background: `
            radial-gradient(
              ${glowSize * 0.8}px circle at var(--pointer-x) var(--pointer-y),
              ${glowColor},
              transparent 50%
            )
          `
        }}
      />
      
      {/* Content */}
      <div 
        className="glow-card-inner"
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          borderRadius: 'inherit',
          backgroundColor: 'inherit'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default GlowCard;
